/**
 * Creates a new logical Storage abstraction of key/value pairs.
 *
 * This function does not operate directly on a physical storage. Instead, it
 * receives an `innerStorage` (which may itself be physical, logical, or a
 * decorated storage) and a `storageLens` that defines how to read and write
 * the logical representation on top of that inner storage.
 *
 * @param {Object} options Configuration for the logical storage.
 *
 * @param {Storage} options.innerStorage
 * The underlying storage on which this logical storage is built. This may be
 * a physical storage (localStorage, sessionStorage, in-memory) or any other
 * storage abstraction implementing the Web Storage API.
 *
 * @param {StorageLens} options.storageLens
 * The lens responsible for transforming the inner storage into a logical
 * storage (for example, mapping a single physical entry into a full hashmap).
 *
 * @returns {Storage}
 * A logical storage implementing the standard Web Storage API
 * (`getItem`, `setItem`, `removeItem`, `key`, `length`, `clear`).
 */
function createLogicalStorage({ innerStorage, storageLens }) {
  const instance = {
    get length() {
      return storageLens.get(innerStorage).length;
    },

    /**
     * Removes all key/value pairs from the storage.
     */
    clear() {
      storageLens.set(null, innerStorage);
    },

    /**
     * Returns the name of the nth key in the storage.
     *
     * @param {number} n
     * The zero-based index of the key to retrieve.
     *
     * @returns {string|null}
     * The name of the nth key, or `null` if the index is out of range.
     */
    key(n) {
      return storageLens.get(innerStorage).key(n);
    },

    /**
     * Returns the value associated with the given key.
     *
     * @param {string} key
     * The key name.
     *
     * @returns {any}
     * The value associated with the key, or `null` if the key does not exist.
     *
     * @throws {TypeError}
     */
    getItem(key) {
      return storageLens.get(innerStorage).getItem(key);
    },

    /**
     * Removes the key/value pair associated with the given key.
     *
     * @param {string} key
     * The key name.
     *
     * @throws {TypeError}
     */
    removeItem(key) {
      const storage = storageLens.get(innerStorage);
      storage.removeItem(key);
      storageLens.set(storage, innerStorage);
    },

    /**
     * Sets the value associated with the given key.
     *
     * If the key already exists, its value is updated.
     * Otherwise, a new key/value pair is created.
     *
     * @param {string} key
     * The key name.
     *
     * @param {any} value
     * The value to store.
     *
     * @returns {any}
     * The stored value.
     *
     * @throws {TypeError}
     */
    setItem(key, value) {
      const storage = storageLens.get(innerStorage);
      storage.setItem(key, value);
      storageLens.set(storage, innerStorage);
      return value;
    },
  };

  return instance;
}

module.exports = createLogicalStorage;
